<!-- GO Commerce API Cache Debug Component -->
<template>
  <!-- Wrap in client-only to ensure it only runs in browser context -->
  <client-only>
    <!-- Debug Toggle Button for Development Environments -->
    <button 
      v-if="showDebugButton && isAllowedToSeeDebug && !showDebug" 
      @click="toggleDebug" 
      class="debug-toggle-button hidden md:block"
    >
      Debug Cache
    </button>
    
    <div class="cache-debug" v-if="showDebug && isAllowedToSeeDebug">
    <div class="cache-debug-header">
      <h3>API Cache Debug</h3>
      <div class="tabs">
        <button :class="{ active: activeTab === 'cache' }" @click="activeTab = 'cache'">Cache</button>
        <button :class="{ active: activeTab === 'monitor' }" @click="activeTab = 'monitor'">API Monitor</button>
      </div>
      <button @click="toggleDebug" class="close-btn">X</button>
    </div>
    <div class="cache-debug-content">
      <!-- Cache Tab -->
      <div v-if="activeTab === 'cache'">
        <div class="cache-status">
          <div class="status-row">
            <div>Cache Enabled:</div>
            <div :class="{ 'enabled': isCacheEnabled, 'disabled': !isCacheEnabled }">
              {{ isCacheEnabled ? 'Yes' : 'No' }}
            </div>
            <button @click="toggleCache" class="toggle-btn">
              {{ isCacheEnabled ? 'Disable' : 'Enable' }}
            </button>
          </div>
        </div>
        
        <div class="endpoint-list">
          <div v-for="endpoint in cachedEndpoints" :key="endpoint.name" class="endpoint-row">
            <div class="endpoint-info" @click="toggleEndpointDetails(endpoint.name)">
              <div class="expand-indicator">{{ expandedEndpoint === endpoint.name ? '▼' : '▶' }}</div>
              <div class="endpoint-name">{{ endpoint.label }}</div>
              <div class="endpoint-ttl">TTL: {{ formatTTL(endpoint.ttl) }}</div>
              <div class="endpoint-actions">
                <button @click.stop="refreshEndpoint(endpoint.name, endpoint.params)" class="refresh-btn">
                  Refresh
                </button>
                <button @click.stop="invalidateEndpoint(endpoint.name)" class="invalidate-btn">
                  Invalidate
                </button>
              </div>
            </div>
            
            <!-- Expanded Endpoint Details -->
            <div class="endpoint-details" v-if="expandedEndpoint === endpoint.name">
              <div v-if="getCacheInfo(endpoint.name)">
                <div><strong>Last Updated:</strong> {{ formatTime(getCacheInfo(endpoint.name).timestamp) }}</div>
                <div><strong>Age:</strong> {{ formatAge(getCacheInfo(endpoint.name).timestamp) }}</div>
                <div><strong>Size:</strong> {{ formatSize(getCacheInfo(endpoint.name).size) }}</div>
                <div><strong>Expires:</strong> {{ formatTime(getCacheInfo(endpoint.name).timestamp + endpoint.ttl) }}</div>
                <div class="cache-data-preview">
                  <strong>Data Sample:</strong>
                  <pre>{{ formatDataPreview(getCacheInfo(endpoint.name).data) }}</pre>
                </div>
              </div>
              <div v-else class="no-cache">No cached data available</div>
            </div>
          </div>
        </div>
        
        <div class="cache-actions">
          <button @click="refreshAllEndpoints" class="refresh-all-btn">Refresh All</button>
          <button @click="invalidateAllCache" class="invalidate-all-btn">Clear All Cache</button>
        </div>
      </div>
      
      <!-- API Monitor Tab -->
      <div v-if="activeTab === 'monitor'">
        <div class="monitor-status">
          <div class="status-row">
            <div>API Monitoring:</div>
            <div :class="{ 'enabled': isMonitoringEnabled, 'disabled': !isMonitoringEnabled }">
              {{ isMonitoringEnabled ? 'Active' : 'Inactive' }}
            </div>
            <button @click="toggleMonitoring" class="toggle-btn">
              {{ isMonitoringEnabled ? 'Stop' : 'Start' }}
            </button>
          </div>
        </div>
        
        <div class="monitor-stats">
          <div class="stats-row">
            <div class="stat-item">
              <span class="stat-label">Total API Calls:</span>
              <span class="stat-value">{{ apiStats.totalCalls }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Monitoring Time:</span>
              <span class="stat-value">{{ formatDuration(apiStats.startTime) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Call Rate:</span>
              <span class="stat-value">{{ calculateCallRate() }} calls/min</span>
            </div>
          </div>
        </div>
        
        <div class="monitor-list">
          <div class="monitor-header">
            <div class="monitor-column endpoint-col">Endpoint</div>
            <div class="monitor-column calls-col">Calls</div>
            <div class="monitor-column cache-hits-col">Cache Hits</div>
            <div class="monitor-column last-call-col">Last Call</div>
          </div>
          <div v-for="endpoint in sortedApiEndpoints" :key="endpoint.name" class="monitor-row">
            <div class="monitor-column endpoint-col" :title="endpoint.name">{{ endpoint.name }}</div>
            <div class="monitor-column calls-col">{{ endpoint.calls }}</div>
            <div class="monitor-column cache-hits-col">
              <span :class="getHitRateClass(endpoint)">
                {{ endpoint.cacheHits }} ({{ calculateHitRate(endpoint) }}%)
              </span>
            </div>
            <div class="monitor-column last-call-col">{{ formatTime(endpoint.lastCall) }}</div>
          </div>
        </div>
        
        <div class="monitor-actions">
          <button @click="resetApiStats" class="reset-btn">Reset Statistics</button>
        </div>
      </div>
    </div>
  </div>
  </client-only>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useBackendService } from '@/composables/services/useBackend.service';
import { useCacheConfigStore } from '@/stores/cacheConfig';
import { useAuth } from '@/composables/useAuth';
import debugTools from '@/utils/debugTools';
import { useRuntimeConfig } from '#app';

const showDebug = ref(false);
const showDebugButton = ref(false);
const backend = useBackendService();
const cacheStore = useCacheConfigStore();
const { user } = useAuth();
const config = useRuntimeConfig();

// Active tab state
const activeTab = ref('cache');

// Endpoint expansion state
const expandedEndpoint = ref(null);

// API monitoring state with localStorage persistence
const STORAGE_KEY = 'go_api_monitor_data';
const isMonitoringEnabled = ref(false);

// Initialize with stored data or defaults
const initApiStats = () => {
  if (typeof window === 'undefined') {
    return {
      totalCalls: 0,
      startTime: Date.now(),
      endpoints: {}
    };
  }
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error loading stored API stats:', e);
  }
  
  return {
    totalCalls: 0,
    startTime: Date.now(),
    endpoints: {}
  };
};

const apiStats = ref(initApiStats());

// Determine if user is allowed to see debug panel
const isAllowedToSeeDebug = computed(() => {
  // Always disable in production unless user has admin role
  if (process.env.NODE_ENV === 'production') {
    return user.value?.role?.type === 'admin' || 
           user.value?.email?.endsWith('@prvidr.com') || 
           user.value?.email?.endsWith('@blueburra.com');
  }
  
  // In development, always allow
  return true;
});

// Secret key sequence for emergency access
let secretKeySequence = [];
const targetSequence = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

// Get the cache status
const isCacheEnabled = computed(() => cacheStore.isCacheEnabled);

// Define all cacheable endpoints
const cachedEndpoints = computed(() => [
  // API endpoints
  { 
    name: 'products', 
    label: 'Product Catalog',
    ttl: cacheStore.getTTL('products'),
    params: {}
  },
  { 
    name: 'network-balance', 
    label: 'Network Balance',
    ttl: cacheStore.getTTL('networkBalance'),
    params: { customerId: user.value?.id || '' }
  },
  { 
    name: 'current-services', 
    label: 'Current Services',
    ttl: cacheStore.getTTL('currentServices'),
    params: {}
  },
  { 
    name: 'user-profile', 
    label: 'User Profile',
    ttl: cacheStore.getTTL('userProfile'),
    params: {}
  },
  { 
    name: 'recharges', 
    label: 'Recharges',
    ttl: cacheStore.getTTL('recharges'),
    params: { customerId: user.value?.id || '' }
  },
  
  // GraphQL queries
  { 
    name: 'graphql_global_settings', 
    label: 'GraphQL: Global Settings',
    ttl: 30 * 60 * 1000, // 30 minutes
    params: {}
  },
  { 
    name: 'graphql:GetFAQs:{}', 
    label: 'GraphQL: FAQs',
    ttl: 60 * 60 * 1000, // 1 hour
    params: {}
  },
  { 
    name: 'graphql:GetProducts:{}', 
    label: 'GraphQL: Products',
    ttl: 30 * 60 * 1000, // 30 minutes
    params: {}
  },
  { 
    name: 'graphql:GetNotifications', 
    label: 'GraphQL: Notifications',
    ttl: 5 * 60 * 1000, // 5 minutes
    params: {}
  },
  { 
    name: 'graphql:GetPageBySlug:{"slug":"home"}', 
    label: 'GraphQL: Home Page',
    ttl: 15 * 60 * 1000, // 15 minutes
    params: {}
  },
  { 
    name: 'graphql:GetExtraPacks:{}', 
    label: 'GraphQL: Extra Packs',
    ttl: 30 * 60 * 1000, // 30 minutes
    params: {}
  },
  { 
    name: 'graphql:GetExtraPacksMore:{}', 
    label: 'GraphQL: Extra Packs More',
    ttl: 30 * 60 * 1000, // 30 minutes
    params: {}
  },
  { 
    name: 'graphql:GetProductBySlug:*', 
    label: 'GraphQL: Product By Slug',
    ttl: 30 * 60 * 1000, // 30 minutes
    params: {}
  }
]);

// Format TTL for display
const formatTTL = (ttl) => {
  const minutes = Math.floor(ttl / 60000);
  if (minutes > 60) {
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
};

// Format timestamp
const formatTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleTimeString();
};

// Format data age
const formatAge = (timestamp) => {
  if (!timestamp) return 'N/A';
  const ageMs = Date.now() - timestamp;
  if (ageMs < 60000) return `${Math.round(ageMs / 1000)}s ago`;
  if (ageMs < 3600000) return `${Math.round(ageMs / 60000)}m ago`;
  return `${Math.round(ageMs / 3600000)}h ago`;
};

// Format data size
const formatSize = (bytes) => {
  if (bytes === undefined || bytes === null) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1048576).toFixed(1)} MB`;
};

// Import existing cache entries into the monitor
const importExistingCacheEntries = () => {
  if (typeof window === 'undefined') return;
  
  try {
    // Get current cache from localStorage
    const cacheData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
    
    // Iterate through cache entries
    Object.keys(cacheData).forEach(key => {
      if (key.startsWith('api_cache_')) {
        // Extract endpoint name from cache key
        let endpoint = key.replace('api_cache_', '');
        
        // Handle POST request endpoints that include parameters in the URL
        // Map integration/network-balance/{customerId} back to integration/network-balance
        // This helps us match what the API monitor shows in the UI
        if (endpoint.startsWith('integration/network-balance/')) {
          console.log(`[ApiMonitor] Detected POST endpoint: ${endpoint}, normalizing to 'integration/network-balance'`);
          endpoint = 'integration/network-balance';
        }
        
        // Calculate cache age
        const cacheEntry = cacheData[key];
        const cacheAge = Date.now() - cacheEntry.timestamp;
        
        // Skip if already tracked in our monitor with accurate data
        if (apiStats.value.endpoints[endpoint] && 
            apiStats.value.endpoints[endpoint].calls > 0 && 
            apiStats.value.endpoints[endpoint].accessCount > 1) {
          return;
        }
        
        // Determine access count (how many times it's been accessed)
        // We'll use this to properly track cache hits
        const accessCount = cacheEntry.accessCount || 1;
        
        // For existing entries, assume first call was a cache miss, subsequent accesses were hits
        const hits = Math.max(0, accessCount - 1);
        
        // Add to our monitoring stats
        apiStats.value.endpoints[endpoint] = {
          name: endpoint,
          calls: accessCount, // Total calls including initial fetch + cache hits
          cacheHits: hits,   // All accesses after the first one are cache hits
          accessCount: accessCount,
          lastCall: cacheEntry.timestamp || Date.now()
        };
        
        // Update total calls counter
        apiStats.value.totalCalls += accessCount;
        
        console.log(`[ApiMonitor] Imported existing cache entry: ${endpoint} with ${hits} cache hits`);
      }
    });
    
    saveApiStats();
  } catch (e) {
    console.error('Error importing existing cache entries:', e);
  }
};

// Format data preview
const formatDataPreview = (data) => {
  if (!data) return 'No data';
  try {
    // Create a simplified preview
    const preview = JSON.stringify(data, (key, value) => {
      // Limit strings to prevent large outputs
      if (typeof value === 'string' && value.length > 50) {
        return value.substring(0, 50) + '...';
      }
      // Skip large arrays
      if (Array.isArray(value) && value.length > 5) {
        return `[Array(${value.length})]`;
      }
      return value;
    }, 2);
    
    // Limit overall size
    return preview.length > 500 ? preview.substring(0, 500) + '...' : preview;
  } catch (e) {
    return 'Error formatting data';
  }
};

// Format monitoring duration
const formatDuration = (startTime) => {
  const durationMs = Date.now() - startTime;
  const seconds = Math.floor(durationMs / 1000);
  
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  }
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

// Toggle debug panel
const toggleDebug = () => {
  showDebug.value = !showDebug.value;
};

// Toggle cache enabled state
const toggleCache = () => {
  if (cacheStore.isCacheEnabled) {
    cacheStore.disableCache();
  } else {
    cacheStore.enableCache();
  }
};

// Toggle endpoint expansion
const toggleEndpointDetails = (endpointName) => {
  if (expandedEndpoint.value === endpointName) {
    expandedEndpoint.value = null;
  } else {
    expandedEndpoint.value = endpointName;
  }
};

// Get cache information from localStorage
const getCacheInfo = (endpointName) => {
  try {
    const cacheData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
    
    // Handle GraphQL cache keys differently
    let cacheKey;
    if (endpointName.startsWith('graphql')) {
      // GraphQL queries are stored with the original query name as the key
      cacheKey = endpointName;
    } else {
      // Regular REST API endpoints use the api_cache_ prefix
      cacheKey = `api_cache_${endpointName}`;
    }
    
    if (cacheData[cacheKey]) {
      // Calculate approximate size
      const size = new Blob([JSON.stringify(cacheData[cacheKey].data)]).size;
      return {
        ...cacheData[cacheKey],
        size
      };
    }
    
    // If not found with direct key, try searching partial match for GraphQL
    if (endpointName.startsWith('graphql')) {
      // Some GraphQL keys might have variations with params
      const graphqlPrefix = endpointName.split(':')[1]; // Get the operation name
      
      // Find any key that contains this operation name
      const matchingKey = Object.keys(cacheData).find(key => 
        key.startsWith('graphql:') && key.includes(graphqlPrefix)
      );
      
      if (matchingKey && cacheData[matchingKey]) {
        const size = new Blob([JSON.stringify(cacheData[matchingKey].data)]).size;
        return {
          ...cacheData[matchingKey],
          size
        };
      }
    }
    
    return null;
  } catch (e) {
    console.error('Error parsing cache:', e);
    return null;
  }
};

// Toggle API monitoring
const toggleMonitoring = () => {
  if (isMonitoringEnabled.value) {
    // Stop monitoring
    stopApiMonitoring();
    // Save monitoring state
    if (typeof window !== 'undefined') {
      localStorage.setItem('go_api_monitor_enabled', 'false');
    }
  } else {
    // Start monitoring
    startApiMonitoring();
    // Save monitoring state
    if (typeof window !== 'undefined') {
      localStorage.setItem('go_api_monitor_enabled', 'true');
    }
  }
};

// Save API stats to localStorage
const saveApiStats = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apiStats.value));
    } catch (e) {
      console.error('Error saving API stats:', e);
    }
  }
};

// Start API monitoring
const startApiMonitoring = () => {
  // Don't reset stats on restart - this allows continuous monitoring
  // Just update the start time if it's a fresh start
  if (apiStats.value.totalCalls === 0) {
    apiStats.value.startTime = Date.now();
  }
  
  // Import existing cache entries into monitor
  importExistingCacheEntries();
  
  // Set up fetch monitoring
  if (typeof window !== 'undefined') {
    // Store originals for both fetch and XMLHttpRequest
    const originalFetch = window.fetch;
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;
    
    // Monitor fetch API
    window.fetch = function(...args) {
      const url = args[0]?.url || args[0];
      const init = args[1] || {};
      
      // Skip monitoring OPTIONS requests
      if (init && init.method === 'OPTIONS') {
        return originalFetch.apply(this, args);
      }
      
      if (typeof url === 'string') {
        // Capture the result promise
        const resultPromise = originalFetch.apply(this, args);
        
        // Check if this is an API call
        if (url.includes('/api/')) {
          try {
            // Extract endpoint name
            const pathname = new URL(url, window.location.origin).pathname;
            const endpoint = pathname.split('/api/')[1];
            
            // Check if this is from cache by checking if it's already in the cache
            // with a valid timestamp (better than relying on the marker alone)
            let isCacheHit = false;
            
            try {
              const cacheData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
              const cacheKey = `api_cache_${endpoint}`;
              
              if (cacheData[cacheKey]) {
                // If we have a valid cache entry with a timestamp that's not too old,
                // and this isn't the first time accessing the endpoint (which would be a cache miss),
                // then it's likely a cache hit
                const accessCount = cacheData[cacheKey].accessCount || 1;
                if (accessCount > 1) {
                  isCacheHit = true;
                }
              }
            } catch (e) {
              // Fallback to window marker if localStorage check fails
              isCacheHit = window.__lastApiCacheHit && 
                          window.__lastApiCacheHit.endpoint === endpoint && 
                          (Date.now() - window.__lastApiCacheHit.timestamp) < 100;
            }
            
            // Record API call
            recordApiCall(endpoint, isCacheHit);
            
            // Wait for the response to get more details
            resultPromise.then(response => {
              // Could record status code, response time, etc. here
              saveApiStats();
            }).catch(err => {
              // Record errors
              if (!apiStats.value.endpoints[endpoint]) {
                apiStats.value.endpoints[endpoint] = {
                  name: endpoint,
                  calls: 0,
                  errors: 0,
                  cacheHits: 0,
                  lastCall: Date.now()
                };
              }
              apiStats.value.endpoints[endpoint].errors = (apiStats.value.endpoints[endpoint].errors || 0) + 1;
              saveApiStats();
            });
          } catch (e) {
            console.error('Error tracking API call:', e);
          }
        }
        
        return resultPromise;
      }
      
      return originalFetch.apply(this, args);
    };
    
    // Monitor XMLHttpRequest for jQuery/axios/etc
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      // Store both method and URL for later use
      this._goApiMonitorUrl = url;
      this._goApiMonitorMethod = method;
      return originalXhrOpen.apply(this, [method, url, ...rest]);
    };
    
    XMLHttpRequest.prototype.send = function(...args) {
      const url = this._goApiMonitorUrl;
      const method = this._goApiMonitorMethod;
      
      // Skip monitoring OPTIONS requests
      if (method === 'OPTIONS') {
        return originalXhrSend.apply(this, args);
      }
      
      if (typeof url === 'string' && url.includes('/api/')) {
        try {
          // Extract endpoint from URL
          const pathname = new URL(url, window.location.origin).pathname;
          const endpoint = pathname.split('/api/')[1];
          
          // Add load listener to detect completion
          this.addEventListener('load', function() {
            const isCacheHit = window.__lastApiCacheHit && 
                              window.__lastApiCacheHit.endpoint === endpoint && 
                              (Date.now() - window.__lastApiCacheHit.timestamp) < 100;
                              
            // Record call with cache info                  
            recordApiCall(endpoint, isCacheHit);
            saveApiStats();
          });
          
          // Add error listener
          this.addEventListener('error', function() {
            if (!apiStats.value.endpoints[endpoint]) {
              apiStats.value.endpoints[endpoint] = {
                name: endpoint,
                calls: 0,
                errors: 0,
                cacheHits: 0,
                lastCall: Date.now()
              };
            }
            apiStats.value.endpoints[endpoint].errors = (apiStats.value.endpoints[endpoint].errors || 0) + 1;
            saveApiStats();
          });
        } catch (e) {
          console.error('Error tracking XHR call:', e);
        }
      }
      
      return originalXhrSend.apply(this, args);
    };
    
    // Store originals for later restoration
    window.__originalFetch = originalFetch;
    window.__originalXhrOpen = originalXhrOpen;
    window.__originalXhrSend = originalXhrSend;
    
    isMonitoringEnabled.value = true;
    saveApiStats();
    
    // Inject a test API call so we can see it working immediately
    console.log('API monitoring started - injecting test call');
    setTimeout(() => {
      recordApiCall('_test/monitor', false);
      saveApiStats();
    }, 500);
  }
};

// Helper to record an API call
const recordApiCall = (endpoint, isCacheHit) => {
  if (!endpoint) return;
  
  // Normalize endpoint for POST requests with dynamic IDs
  // This ensures all network-balance/{id} calls count towards the same endpoint stats
  let normalizedEndpoint = endpoint;
  
  // Extract the base endpoint for network-balance calls with dynamic IDs
  if (endpoint.startsWith('integration/network-balance/')) {
    normalizedEndpoint = 'integration/network-balance';
    console.log(`[ApiMonitor] Normalizing endpoint ${endpoint} to ${normalizedEndpoint}`);
  }
  
  // Update total calls
  apiStats.value.totalCalls++;
  
  // Ensure endpoint entry exists
  if (!apiStats.value.endpoints[normalizedEndpoint]) {
    apiStats.value.endpoints[normalizedEndpoint] = {
      name: normalizedEndpoint,
      calls: 0,
      errors: 0,
      cacheHits: 0,
      accessCount: 0,
      lastCall: Date.now()
    };
  }
  
  // Update stats
  apiStats.value.endpoints[normalizedEndpoint].calls++;
  apiStats.value.endpoints[normalizedEndpoint].accessCount = 
    (apiStats.value.endpoints[normalizedEndpoint].accessCount || 0) + 1;
  apiStats.value.endpoints[normalizedEndpoint].lastCall = Date.now();
  
  if (isCacheHit) {
    apiStats.value.endpoints[normalizedEndpoint].cacheHits++;
  }
  
  // Also check if any endpoint in the cache with this base name has been accessed
  try {
    const cacheData = JSON.parse(localStorage.getItem('go_api_cache') || '{}');
    
    // For network-balance, check all potential ID-based cache keys
    if (normalizedEndpoint === 'integration/network-balance') {
      // Look for any cache key matching the network-balance pattern
      Object.keys(cacheData).forEach(key => {
        if (key.startsWith('api_cache_integration/network-balance/')) {
          // Update access count for this specific ID-based entry
          cacheData[key].accessCount = (cacheData[key].accessCount || 1) + 1;
          console.log(`[ApiMonitor] Updated access count for ${key}`);
          // Record that we've found a cache hit
          if (!isCacheHit) {
            apiStats.value.endpoints[normalizedEndpoint].cacheHits++;
          }
        }
      });
      localStorage.setItem('go_api_cache', JSON.stringify(cacheData));
    } else {
      // Regular endpoint handling
      const cacheKey = `api_cache_${endpoint}`;
      if (cacheData[cacheKey]) {
        cacheData[cacheKey].accessCount = (cacheData[cacheKey].accessCount || 1) + 1;
        localStorage.setItem('go_api_cache', JSON.stringify(cacheData));
      }
    }
  } catch (e) {
    console.error('Error updating cache access counts:', e);
  }
};

// Stop API monitoring
const stopApiMonitoring = () => {
  if (typeof window !== 'undefined') {
    if (window.__originalFetch) {
      window.fetch = window.__originalFetch;
    }
    
    if (window.__originalXhrOpen && window.__originalXhrSend) {
      XMLHttpRequest.prototype.open = window.__originalXhrOpen;
      XMLHttpRequest.prototype.send = window.__originalXhrSend;
    }
    
    isMonitoringEnabled.value = false;
    saveApiStats();
  }
};

// Reset API statistics
const resetApiStats = () => {
  apiStats.value = {
    totalCalls: 0,
    startTime: Date.now(),
    endpoints: {}
  };
  saveApiStats();
};

// Calculate call rate
const calculateCallRate = () => {
  const durationMin = (Date.now() - apiStats.value.startTime) / 60000;
  if (durationMin < 0.1) return '0.0';
  return (apiStats.value.totalCalls / durationMin).toFixed(1);
};

// Calculate cache hit rate for an endpoint
const calculateHitRate = (endpoint) => {
  if (!endpoint || endpoint.calls === 0) return '0';
  return Math.round((endpoint.cacheHits / endpoint.calls) * 100);
};

// Get color class based on hit rate
const getHitRateClass = (endpoint) => {
  const hitRate = calculateHitRate(endpoint);
  if (hitRate >= 80) return 'hit-rate-high';
  if (hitRate >= 50) return 'hit-rate-medium';
  return 'hit-rate-low';
};

// Refresh a specific endpoint
const refreshEndpoint = async (endpoint, params) => {
  try {
    console.log(`[CacheDebug] Refreshing ${endpoint}...`);
    await backend.refreshEndpoint(endpoint, params);
    console.log(`[CacheDebug] Successfully refreshed ${endpoint}`);
  } catch (error) {
    console.error(`[CacheDebug] Error refreshing ${endpoint}:`, error);
  }
};

// Invalidate a specific endpoint
const invalidateEndpoint = (endpoint) => {
  backend.invalidateCache(endpoint);
  console.log(`[CacheDebug] Invalidated cache for ${endpoint}`);
};

// Refresh all endpoints
const refreshAllEndpoints = async () => {
  console.log('[CacheDebug] Refreshing all endpoints...');
  for (const endpoint of cachedEndpoints.value) {
    try {
      await backend.refreshEndpoint(endpoint.name, endpoint.params);
      console.log(`[CacheDebug] Refreshed ${endpoint.name}`);
    } catch (error) {
      console.error(`[CacheDebug] Error refreshing ${endpoint.name}:`, error);
    }
  }
  console.log('[CacheDebug] Finished refreshing all endpoints');
};

// Clear all cache
const invalidateAllCache = () => {
  backend.invalidateAllCache();
  console.log('[CacheDebug] Cleared all cache entries');
};

// Listen for debug events from the debug tools utility
onMounted(() => {
  // Check for server-side rendering
  if (typeof window === 'undefined') return;
  
  // In development or on localhost, show the debug button
  showDebugButton.value = 
    process.env.NODE_ENV === 'development' || 
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1' 
    // || 
    // window.location.hostname.includes('prvidr.io');

  // Check if monitoring was enabled before page refresh
  if (typeof window !== 'undefined') {
    try {
      const wasMonitoring = localStorage.getItem('go_api_monitor_enabled') === 'true';
      if (wasMonitoring) {
        // Auto-start monitoring if it was enabled before refresh
        setTimeout(() => {
          startApiMonitoring();
        }, 1000); // Give a moment for everything to initialize
      }
    } catch (e) {
      console.error('Error checking monitoring state:', e);
    }
  }

  // Subscribe to the toggle event
  debugTools.onToggleCacheDebug(() => {
    // Only allow toggling if user is allowed to see debug
    if (isAllowedToSeeDebug.value) {
      showDebug.value = !showDebug.value;
    } else if (process.env.NODE_ENV !== 'production') {
      console.warn('Cache debug panel is only available to admin users or in development environments');
    }
  });
  
  // Subscribe to the reset API stats event
  debugTools.onResetApiStats(() => {
    resetApiStats();
  });
  
  // Subscribe to the clear all cache event
  debugTools.onClearAllCache(() => {
    invalidateAllCache();
  });
  
  // In production, also implement a secure Konami-code-like secret for emergency access
  if (process.env.NODE_ENV === 'production') {
    const handleKeydown = (e) => {
      secretKeySequence.push(e.key);
      secretKeySequence = secretKeySequence.slice(-targetSequence.length);
      
      if (secretKeySequence.join(',') === targetSequence.join(',')) {
        const password = prompt('Enter GO debug access password:');
        if (password === 'PrvidrGOCache2025') { // In real implementation, use a more secure approach
          showDebug.value = true;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeydown);
    
    // Clean up event listener on unmount
    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);
    });
  }
});
// Computed property for sorted API endpoints
const sortedApiEndpoints = computed(() => {
  return Object.values(apiStats.value.endpoints)
    .sort((a, b) => b.calls - a.calls);
});
</script>

<style scoped>
.cache-debug {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 580px; /* Increased width for better readability */
  max-height: 600px; /* Increased height to show more content */
  background-color: rgba(255, 255, 255, 0.95);
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.cache-debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f0f0f0;
  border-bottom: 1px solid #ddd;
}

.tabs {
  display: flex;
  margin-left: 20px;
}

.tabs button {
  padding: 5px 10px;
  border: none;
  background: transparent;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  font-size: 12px;
}

.tabs button.active {
  background-color: #9900F1;
  color: white;
  border-color: #bbdefb;
  font-weight: bold;
}

.cache-debug-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 5px;
}

.cache-debug-content {
  padding: 15px;
  overflow-y: auto;
}

.cache-status {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.enabled {
  color: #4caf50;
  font-weight: bold;
}

.disabled {
  color: #f44336;
  font-weight: bold;
}

.endpoint-list {
  margin-bottom: 15px;
}

.endpoint-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.endpoint-info {
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
}

.expand-indicator {
  margin-right: 5px;
  font-size: 10px;
  color: #666;
}

.endpoint-name {
  font-weight: 500;
  flex: 1;
}

.endpoint-ttl {
  margin-right: 10px;
  color: #666;
  font-size: 12px;
}

.endpoint-actions {
  display: flex;
  gap: 5px;
}

.endpoint-details {
  padding: 8px;
  margin-top: 4px;
  background-color: #f8f8f8;
  border-radius: 4px;
  font-size: 12px;
}

.cache-data-preview {
  margin-top: 8px;
}

.cache-data-preview pre {
  max-height: 100px;
  overflow: auto;
  padding: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  margin-top: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}

.no-cache {
  color: #999;
  font-style: italic;
}

.cache-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

button {
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #ccc;
  background-color: #f8f8f8;
}

.refresh-btn, .refresh-all-btn {
  background-color: #e3f2fd;
  color: #2196f3;
  border-color: #bbdefb;
}

.invalidate-btn, .invalidate-all-btn {
  background-color: #ffebee;
  color: #f44336;
  border-color: #ffcdd2;
}

.toggle-btn {
  background-color: #e8f5e9;
  color: #4caf50;
  border-color: #c8e6c9;
}

/* API Monitor Styles */
.monitor-status {
  margin-bottom: 15px;
  padding: 10px;
  border-bottom: 1px solid #eee;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.monitor-stats {
  margin-bottom: 15px;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  flex-wrap: wrap;
}

.stat-item {
  background-color: #f9f9f9;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 5px;
  flex: 1;
  margin-right: 8px;
  min-width: 120px;
  display: flex;
  flex-direction: column;
}

.stat-item:last-child {
  margin-right: 0;
}

.stat-label {
  font-weight: bold;
  color: #666;
  margin-bottom: 2px;
}

.stat-value {
  font-size: 14px;
  color: #333;
}

.monitor-list {
  margin-bottom: 15px;
  max-height: 380px; /* Increased height to show more entries */
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 4px;
}

.monitor-header {
  display: flex;
  font-weight: bold;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  padding: 8px 10px;
  font-size: 12px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.monitor-row {
  display: flex;
  padding: 6px 10px;
  border-bottom: 1px solid #eee;
  font-size: 12px;
  align-items: center;
}

.monitor-row:hover {
  background-color: #f9f9f9;
}

.monitor-column {
  padding-right: 8px;
}

/* Custom column widths */
.monitor-column.endpoint-col {
  flex: 3; /* Give endpoint column more space */
  min-width: 0; /* Required for text-overflow to work */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.monitor-column.calls-col {
  flex: 0.8;
  text-align: center;
}

.monitor-column.cache-hits-col {
  flex: 1.2;
  text-align: center;
}

.monitor-column.last-call-col {
  flex: 1.5;
  text-align: right;
}

.hit-rate-high {
  color: #4caf50;
}

.hit-rate-medium {
  color: #ff9800;
}

.hit-rate-low {
  color: #f44336;
}

.reset-btn {
  background-color: #f5f5f5;
  color: #666;
  border-color: #ddd;
}

/* Debug Toggle Button (Only shows in development) */
.debug-toggle-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 8px 12px;
  background-color: #9900F1;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  z-index: 9999;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
}

.debug-toggle-button:hover {
  background-color: #7d00c5;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}
</style>
